import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Divider,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  CardGiftcard as GiftIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  MoreVert as MoreVertIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Computer as ComputerIcon,
  Wifi as WifiIcon,
  CloudDone as CloudDoneIcon,
  BugReport as BugReportIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Games as GamesIcon,
  EmojiEvents as EventsIcon,
  Token as TokenIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';
import api from '../services/api';

interface DashboardStats {
  // User Statistics
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  newUsersThisWeek: number;
  newUsersThisMonth: number;
  
  // Agent Statistics
  totalAgents: number;
  activeAgents: number;
  
  // Benefits & Redemptions
  totalBenefits: number;
  activeBenefits: number;
  totalRedemptions: number;
  pendingRedemptions: number;
  completedRedemptions: number;
  redemptionsToday: number;
  
  // Giveaways & Games
  activeGiveaways: number;
  totalGiveaways: number;
  totalGames: number;
  activeGames: number;
  
  // Financial
  totalRevenue: number;
  monthlyRevenue: number;
  
  // System Health
  systemStatus: string;
  databaseStatus: string;
}

interface SystemAlert {
  id: string;
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: string;
  priority: 'high' | 'medium' | 'low';
  resolved: boolean;
}

interface RecentActivity {
  id: string;
  type: 'user_signup' | 'redemption' | 'giveaway' | 'game_play' | 'admin_action';
  description: string;
  user: string;
  timestamp: string;
  status: string;
}

const SuperAdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [alertDialog, setAlertDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<SystemAlert | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchDashboardData();
    fetchSystemAlerts();
    fetchRecentActivity();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchSystemAlerts();
      fetchRecentActivity();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemAlerts = async () => {
    // Mock system alerts for demonstration
    const mockAlerts: SystemAlert[] = [
      {
        id: 'alert-001',
        type: 'warning',
        title: 'High Memory Usage',
        message: 'Server memory usage is at 85%. Consider scaling resources.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        priority: 'high',
        resolved: false
      },
      {
        id: 'alert-002',
        type: 'info',
        title: 'Database Backup Completed',
        message: 'Daily database backup completed successfully.',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
        priority: 'low',
        resolved: true
      },
      {
        id: 'alert-003',
        type: 'error',
        title: 'Payment Gateway Error',
        message: '3 failed payment transactions detected. Check payment processor.',
        timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        priority: 'high',
        resolved: false
      },
      {
        id: 'alert-004',
        type: 'success',
        title: 'System Update Complete',
        message: 'Security patches applied successfully.',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
        priority: 'medium',
        resolved: true
      }
    ];
    setAlerts(mockAlerts);
  };

  const fetchRecentActivity = async () => {
    // Mock recent activity for demonstration
    const mockActivity: RecentActivity[] = [
      {
        id: 'activity-001',
        type: 'user_signup',
        description: 'New user registered',
        user: 'john.doe@example.com',
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
        status: 'completed'
      },
      {
        id: 'activity-002',
        type: 'redemption',
        description: 'Benefit redemption approved',
        user: 'jane.smith@example.com',
        timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
        status: 'approved'
      },
      {
        id: 'activity-003',
        type: 'giveaway',
        description: 'New giveaway created',
        user: 'admin@plugvaultplus.com',
        timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        status: 'active'
      },
      {
        id: 'activity-004',
        type: 'game_play',
        description: 'Spin wheel game played',
        user: 'mike.johnson@example.com',
        timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
        status: 'won'
      },
      {
        id: 'activity-005',
        type: 'admin_action',
        description: 'System settings updated',
        user: 'super.admin@plugvaultplus.com',
        timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
        status: 'completed'
      }
    ];
    setRecentActivity(mockActivity);
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'success': return <CheckCircleIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'success': return 'success';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_signup': return <PersonIcon />;
      case 'redemption': return <GiftIcon />;
      case 'giveaway': return <EventsIcon />;
      case 'game_play': return <GamesIcon />;
      case 'admin_action': return <SettingsIcon />;
      default: return <InfoIcon />;
    }
  };

  const handleMarkResolved = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, resolved: true } : alert
    ));
  };

  // Chart data
  const userGrowthData = [
    { name: 'Jan', users: 120 },
    { name: 'Feb', users: 145 },
    { name: 'Mar', users: 180 },
    { name: 'Apr', users: 220 },
    { name: 'May', users: 280 },
    { name: 'Jun', users: 350 },
    { name: 'Jul', users: 420 },
  ];

  const systemHealthData = [
    { name: 'CPU', value: 65, color: '#8884d8' },
    { name: 'Memory', value: 85, color: '#82ca9d' },
    { name: 'Storage', value: 45, color: '#ffc658' },
    { name: 'Network', value: 92, color: '#ff7c7c' }
  ];

  const revenueData = [
    { name: 'Week 1', revenue: 2400, users: 240 },
    { name: 'Week 2', revenue: 1398, users: 220 },
    { name: 'Week 3', revenue: 9800, users: 290 },
    { name: 'Week 4', revenue: 3908, users: 300 },
  ];

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SecurityIcon color="primary" />
            Super Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            System overview and management console
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchDashboardData}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ 
              bgcolor: alerts.filter(a => !a.resolved && a.priority === 'high').length > 0 ? 'error.main' : 'default',
              color: alerts.filter(a => !a.resolved && a.priority === 'high').length > 0 ? 'white' : 'default'
            }}
          >
            <Badge badgeContent={alerts.filter(a => !a.resolved).length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Quick Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.totalUsers || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Users
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    +{stats?.newUsersToday || 0} today
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <PeopleIcon fontSize="large" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.totalRedemptions || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Redemptions
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    {stats?.pendingRedemptions || 0} pending
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <GiftIcon fontSize="large" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    R{(stats?.totalRevenue || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Total Revenue
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    R{(stats?.monthlyRevenue || 0).toLocaleString()} this month
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <MoneyIcon fontSize="large" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats?.activeGiveaways || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Active Giveaways
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                    {stats?.totalGiveaways || 0} total
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <EventsIcon fontSize="large" />
                </Avatar>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* System Health */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon color="primary" />
                System Health
              </Typography>
              <Grid container spacing={2}>
                {systemHealthData.map((item) => (
                  <Grid item xs={6} key={item.name}>
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{item.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.value}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={item.value} 
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: item.value > 80 ? '#f44336' : item.value > 60 ? '#ff9800' : '#4caf50'
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<CloudDoneIcon />}
                  label={`Database: ${stats?.databaseStatus || 'Unknown'}`}
                  color={stats?.databaseStatus === 'connected' ? 'success' : 'warning'}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  icon={<WifiIcon />}
                  label="API: Operational"
                  color="success"
                  variant="outlined"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* User Growth Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="primary" />
                User Growth (7 Days)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="users" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* System Alerts */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="primary" />
                System Alerts
                <Badge badgeContent={alerts.filter(a => !a.resolved).length} color="error">
                  <Box />
                </Badge>
              </Typography>
              <List>
                {alerts.slice(0, 5).map((alert) => (
                  <ListItem
                    key={alert.id}
                    sx={{ 
                      bgcolor: alert.resolved ? 'background.paper' : 'action.hover',
                      borderRadius: 1,
                      mb: 1,
                      border: `1px solid ${alert.resolved ? 'transparent' : 
                        alert.priority === 'high' ? '#f44336' : 
                        alert.priority === 'medium' ? '#ff9800' : '#4caf50'}`
                    }}
                  >
                    <ListItemIcon>
                      <Avatar 
                        sx={{ 
                          bgcolor: `${getAlertColor(alert.type)}.main`,
                          width: 32, 
                          height: 32 
                        }}
                      >
                        {getAlertIcon(alert.type)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={alert.title}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {alert.message}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(alert.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label={alert.priority}
                          size="small"
                          color={alert.priority === 'high' ? 'error' : alert.priority === 'medium' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                        {!alert.resolved && (
                          <Button
                            size="small"
                            onClick={() => handleMarkResolved(alert.id)}
                          >
                            Resolve
                          </Button>
                        )}
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="primary" />
                Recent Activity
              </Typography>
              <List>
                {recentActivity.slice(0, 6).map((activity) => (
                  <ListItem key={activity.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.light' }}>
                        {getActivityIcon(activity.type)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.description}
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {activity.user}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(activity.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <Chip
                      label={activity.status}
                      size="small"
                      color={activity.status === 'completed' || activity.status === 'approved' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Chart */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AnalyticsIcon color="primary" />
                Revenue & User Analytics
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="revenue" fill="#8884d8" name="Revenue (R)" />
                  <Bar yAxisId="right" dataKey="users" fill="#82ca9d" name="New Users" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Notifications Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: { width: 300, maxHeight: 400 }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            System Notifications
          </Typography>
          <Divider />
        </Box>
        {alerts.filter(a => !a.resolved).slice(0, 3).map((alert) => (
          <MenuItem 
            key={alert.id} 
            onClick={() => {
              setSelectedAlert(alert);
              setAlertDialog(true);
              setAnchorEl(null);
            }}
            sx={{ 
              flexDirection: 'column', 
              alignItems: 'flex-start',
              whiteSpace: 'normal',
              py: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {getAlertIcon(alert.type)}
              <Typography variant="subtitle2" noWrap>
                {alert.title}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {alert.message.substring(0, 50)}...
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Alert Detail Dialog */}
      <Dialog
        open={alertDialog}
        onClose={() => setAlertDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedAlert && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getAlertIcon(selectedAlert.type)}
              {selectedAlert.title}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedAlert && (
            <Box>
              <Typography variant="body1" gutterBottom>
                {selectedAlert.message}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Priority: {selectedAlert.priority} | 
                  Time: {new Date(selectedAlert.timestamp).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertDialog(false)}>
            Close
          </Button>
          {selectedAlert && !selectedAlert.resolved && (
            <Button 
              onClick={() => {
                handleMarkResolved(selectedAlert.id);
                setAlertDialog(false);
              }}
              variant="contained"
            >
              Mark Resolved
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminDashboard;